import Config from "../_config/config";
import myAxios from "./axiositeachWrapper";

const BASE = `${Config.nodeSiteUrl}v1/campaigns`;
const TEMPLATES_BASE = `${Config.nodeSiteUrl}v1/email-templates`;

class CampaignService {
  list() {
    return myAxios.get<any>(BASE);
  }

  create(body: any) {
    return myAxios.post<any>(BASE, body);
  }

  update(id: number, body: any) {
    return myAxios.put<any>(`${BASE}/${id}`, body);
  }

  delete(id: number) {
    return myAxios.delete<any>(`${BASE}/${id}`);
  }

  getEnquiries(id: number, page = 1, limit = 50) {
    return myAxios.get<any>(`${BASE}/${id}/enquiries`, { params: { page, limit } });
  }

  getAnalytics(id: number) {
    return myAxios.get<any>(`${BASE}/${id}/analytics`);
  }

  sendEmail(
    campaignId: number,
    body: {
      subject: string;
      htmlBody: string;
      templateId?: number;
      // B2C audience selectors
      sendTo?: string[];
      // B2B audience selectors (ignored by B2C campaigns and vice-versa)
      companyIds?: number[];
      sectorIds?: number[];
      locationIds?: number[];
      empanelmentStatusIds?: number[];
      contactStatusIds?: number[];
      designationIds?: number[];
      contactPersonIds?: number[];
    },
  ) {
    return myAxios.post<any>(`${BASE}/${campaignId}/send-email`, body);
  }

  getCompanies(id: number, page = 1, limit = 50) {
    return myAxios.get<any>(`${BASE}/${id}/companies`, { params: { page, limit } });
  }

  previewB2BRecipients(
    campaignId: number,
    body: {
      companyIds?: number[];
      sectorIds?: number[];
      locationIds?: number[];
      empanelmentStatusIds?: number[];
      contactStatusIds?: number[];
      designationIds?: number[];
      contactPersonIds?: number[];
    },
  ) {
    return myAxios.post<any>(`${BASE}/${campaignId}/b2b-recipients-preview`, body);
  }

  // ── Participants (B2B target list) ──────────────────────────────────────
  listParticipants(campaignId: number, includeRemoved = false) {
    return myAxios.get<any>(`${BASE}/${campaignId}/participants`, {
      params: includeRemoved ? { includeRemoved: 1 } : {},
    });
  }
  previewParticipants(
    campaignId: number,
    body: {
      companyIds?: number[];
      contactPersonIds?: number[];
      sectorIds?: number[];
      locationIds?: number[];
      empanelmentStatusIds?: number[];
      contactStatusIds?: number[];
      stageIds?: number[];
    },
  ) {
    return myAxios.post<any>(`${BASE}/${campaignId}/participants/preview`, body);
  }
  bulkAddParticipants(
    campaignId: number,
    body: {
      companyIds?: number[];
      contactPersonIds?: number[];
      sectorIds?: number[];
      locationIds?: number[];
      empanelmentStatusIds?: number[];
      contactStatusIds?: number[];
      stageIds?: number[];
      notes?: string;
    },
  ) {
    return myAxios.post<any>(`${BASE}/${campaignId}/participants/bulk-add`, body);
  }
  removeParticipant(campaignId: number, participantId: number) {
    return myAxios.delete<any>(`${BASE}/${campaignId}/participants/${participantId}`);
  }

  // ── Drip / Multi-step Sequence ──────────────────────────────────────────
  listSequenceSteps(campaignId: number) {
    return myAxios.get<any>(`${BASE}/${campaignId}/sequence/steps`);
  }
  upsertSequenceStep(
    campaignId: number,
    body: {
      id?: number;
      stepIndex: number;
      parentStepIndex?: number | null;
      delayDays: number;
      triggerCondition?: "ALWAYS" | "IF_NOT_OPENED_PREV" | "IF_OPENED_NOT_CLICKED_PREV" | "IF_NOT_REPLIED_PREV";
      subject: string;
      bodyHtml: string;
      templateId?: number;
    },
  ) {
    return myAxios.post<any>(`${BASE}/${campaignId}/sequence/steps`, body);
  }

  // ── Hot Leads (B2C engagement view) ─────────────────────────────────────
  getHotLeads(params?: { windowDays?: number; limit?: number; stageId?: number; hideReplied?: 0 | 1 }) {
    return myAxios.get<any>(`${BASE}/hot-leads`, { params });
  }
  deleteSequenceStep(campaignId: number, stepId: number) {
    return myAxios.delete<any>(`${BASE}/${campaignId}/sequence/steps/${stepId}`);
  }
  getSequenceEnrollments(campaignId: number) {
    return myAxios.get<any>(`${BASE}/${campaignId}/sequence/enrollments`);
  }
  getSequenceMetrics(campaignId: number) {
    return myAxios.get<any>(`${BASE}/${campaignId}/sequence/metrics`);
  }
  processDueSequences() {
    return myAxios.post<any>(`${BASE}/sequence/process-due`);
  }

  // ── AI Sequence Builder ────────────────────────────────────────────────
  aiSequenceChat(body: {
    campaignId: number;
    conversationHistory: Array<{ role: "user" | "assistant"; content: string }>;
    mode?: "full" | "incremental";
  }) {
    return myAxios.post<any>(`${BASE}/ai/sequence-chat`, body);
  }
  aiSequenceGenerate(body: {
    campaignId: number;
    mode: "full" | "incremental";
    conversationContext?: Array<{ role: "user" | "assistant"; content: string }>;
    instruction?: string;
  }) {
    return myAxios.post<any>(`${BASE}/ai/sequence-generate`, body);
  }

  getEmailTemplates() {
    return myAxios.get<any>(`${BASE}/email-templates`);
  }

  getConversation(campaignId: number, enquiryId: number) {
    return myAxios.get<any>(`${BASE}/${campaignId}/enquiries/${enquiryId}/conversation`);
  }

  replyToLead(campaignId: number, enquiryId: number, body: { subject: string; htmlBody: string }) {
    return myAxios.post<any>(`${BASE}/${campaignId}/enquiries/${enquiryId}/reply`, body);
  }

  // Email Template Builder methods
  listTemplates() {
    return myAxios.get<any>(TEMPLATES_BASE);
  }

  getTemplate(id: number) {
    return myAxios.get<any>(`${TEMPLATES_BASE}/${id}`);
  }

  createTemplate(body: { Name: string; Subject?: string; Message?: string; DesignJson?: string }) {
    return myAxios.post<any>(TEMPLATES_BASE, body);
  }

  updateTemplate(id: number, body: { Name: string; Subject?: string; Message?: string; DesignJson?: string }) {
    return myAxios.put<any>(`${TEMPLATES_BASE}/${id}`, body);
  }

  deleteTemplate(id: number) {
    return myAxios.delete<any>(`${TEMPLATES_BASE}/${id}`);
  }

  duplicateTemplate(id: number) {
    return myAxios.post<any>(`${TEMPLATES_BASE}/${id}/duplicate`);
  }

  getStarterTemplates() {
    return myAxios.get<any>(`${TEMPLATES_BASE}/starters`);
  }

  sendTestEmail(id: number, body: { email: string }) {
    return myAxios.post<any>(`${TEMPLATES_BASE}/${id}/send-test`, body);
  }
}

export default new CampaignService();
